import type { MarketType } from '@app/shared'

export type TradeSide = 'long' | 'short'

/** The terminal's lower tabs a link may open on; Positions is the default and needs no query. */
export type TerminalTab = 'copy'

export interface TerminalPathOptions {
  market?: string
  type?: MarketType
  side?: TradeSide
  /** `positions`: land on the open positions below the form, not on the form itself. */
  view?: 'positions'
  /** `copy`: open the Copy trading tab under the form (the signal-code step). */
  tab?: TerminalTab
  /** A signal code to fill in and check at once; only meaningful with `tab: 'copy'`. */
  code?: string
}

/**
 * The market signal codes trade: gold on the perpetual. A code decides its
 * own market, so this is only where the terminal opens for the Copy trading
 * tab — the pair on screen above the code field.
 */
export const COPY_TRADING_MARKET = 'XAU/USDT-PERP'

/**
 * The trading tab is a menu; every route into the actual order form goes
 * through this helper so a market row cannot accidentally send someone back
 * to the menu and discard the pair they chose.
 */
export function terminalPath(options: TerminalPathOptions = {}): string {
  const query = new URLSearchParams()

  if (options.market !== undefined && options.market !== '') query.set('market', options.market)
  if (options.type !== undefined) query.set('type', options.type)
  if (options.side !== undefined) query.set('side', options.side)
  if (options.view !== undefined) query.set('view', options.view)
  if (options.tab !== undefined) query.set('tab', options.tab)
  if (options.code !== undefined && options.code !== '') query.set('code', options.code)

  const suffix = query.toString()
  return suffix === '' ? '/trade/terminal' : `/trade/terminal?${suffix}`
}

/**
 * Where a signal code is entered since the Signals tab was dissolved (the
 * owner's call after the 28 September demo): the terminal on gold, with the
 * Copy trading tab open. A code from the channel, the bot or a notice is
 * filled in and checked straight away, which opens its confirmation sheet.
 */
export function copyTradingPath(code?: string | null): string {
  return terminalPath({
    market: COPY_TRADING_MARKET,
    type: 'futures',
    tab: 'copy',
    ...(code === undefined || code === null || code === '' ? {} : { code }),
  })
}

/**
 * Whether the back chevron has somewhere in the app to go.
 *
 * React Router keys the first entry of a session "default"; every in-app
 * navigation, and a reload of one, carries a generated key. A screen opened
 * from a bookmark, a push notification or a home-screen icon is therefore the
 * one case with nothing behind it, and history-back there does nothing.
 */
export function hasInAppHistory(locationKey: string): boolean {
  return locationKey !== 'default'
}

/**
 * The pair the terminal opens on.
 *
 * A row hands over an exact pair; an action-grid tile hands over only a type;
 * an old bookmark may carry neither, and then a perpetual is the default. A
 * pair asked for by name that the registry does not have is *not* swapped for
 * another one: the caller shows "no such pair" instead, because a stale link
 * quietly opening a different market under a leveraged form is worse than a
 * dead end with a way out.
 */
export function resolveMarket<M extends { symbol: string; marketType: MarketType }>(
  tradable: readonly M[],
  requested: string | null,
  requestedType: MarketType | null,
): M | null {
  if (requested !== null && requested !== '') {
    return tradable.find((item) => item.symbol === requested) ?? null
  }
  if (requestedType !== null) {
    const ofType = tradable.find((item) => item.marketType === requestedType)
    if (ofType !== undefined) return ofType
  }
  return tradable.find((item) => item.marketType === 'futures') ?? tradable[0] ?? null
}
